import React, { createContext, useContext, useEffect, ReactNode } from 'react';

interface PiSDKContextType {
  isPiAvailable: boolean;
  isPiBrowser: boolean;
  sdkReady: boolean;
  error?: string;
}

const PiSDKContext = createContext<PiSDKContextType>({
  isPiAvailable: false,
  isPiBrowser: false,
  sdkReady: false,
});

interface PiSDKProviderProps {
  children: ReactNode;
}

export const PiSDKProvider: React.FC<PiSDKProviderProps> = ({ children }) => {
  const [contextValue, setContextValue] = React.useState<PiSDKContextType>({
    isPiAvailable: false,
    isPiBrowser: false,
    sdkReady: false,
  });

  useEffect(() => {
    const checkPiSDK = () => {
      try {
        const userAgent = navigator.userAgent;
        const isPiBrowser = userAgent.includes('PiBrowser') || 
                           userAgent.includes('Pi Network') ||
                           userAgent.includes('Pi/') ||
                           window.location.hostname.includes('pi://') ||
                           (typeof window !== 'undefined' && (window as any).Pi);
        
        // Check if Pi SDK is loaded and available
        const isPiAvailable = typeof window !== 'undefined' && 
                             (window as any).Pi && 
                             typeof (window as any).Pi.authenticate === 'function' &&
                             !(window as any).piSDKError;
        
        const sdkReady = isPiAvailable && ((window as any).piSDKReady || (window as any).piSDKLoaded !== false);
        
        // Get error message if any
        const error = (window as any).piSDKError?.message;
        
        console.log('Pi SDK Check:', {
          userAgent,
          isPiBrowser,
          isPiAvailable,
          sdkReady,
          piSDKLoaded: (window as any).piSDKLoaded,
          piSDKReady: (window as any).piSDKReady,
          piSDKError: (window as any).piSDKError?.message || null
        });
        
        setContextValue({
          isPiAvailable,
          isPiBrowser,
          sdkReady,
          error
        });
      } catch (err) {
        console.warn('Error checking Pi SDK:', err);
        setContextValue({
          isPiAvailable: false,
          isPiBrowser: false,
          sdkReady: false,
          error: 'Failed to check Pi SDK status'
        });
      }
    };

    // Check immediately
    checkPiSDK();
    
    // Also check after delays to ensure SDK has loaded
    const timeouts = [1000, 3000, 5000].map(delay => 
      setTimeout(checkPiSDK, delay)
    );
    
    // Add error event listener to catch global errors
    const handleError = (event: ErrorEvent) => {
      if (event.message && event.message.includes('pi-sdk')) {
        console.warn('Pi SDK error caught:', event.message);
        setContextValue(prev => ({
          ...prev,
          error: 'Pi SDK communication error - please try again'
        }));
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <PiSDKContext.Provider value={contextValue}>
      {children}
    </PiSDKContext.Provider>
  );
};

export const usePiSDK = (): PiSDKContextType => {
  const context = useContext(PiSDKContext);
  if (context === undefined) {
    throw new Error('usePiSDK must be used within a PiSDKProvider');
  }
  return context;
};